import { useQuery } from '@tanstack/react-query'
import { EndpointHint, LoadFailure, formatDateTime, Skeleton } from '../../core/index.js'
import type { AccountsAdminApi } from '../adminApi.js'
import { describeSends } from './AdminsPage.js'

const KIND_LABEL = { admin: 'Admin', operator: 'Operator', facility_administrator: 'Facility administrator', worker: 'Worker' } as const

/**
 * System › Invitations (invitations plan section 5): every pending
 * invitation on the platform, by kind and client, with its age and what
 * went out. Resending and withdrawing stay with whoever may invite that
 * kind: the Admins page for admins, the client's pages for the rest.
 */
export function InvitationsPage({ api }: { api: Pick<AccountsAdminApi, 'listPendingInvitations'> }) {
  const query = useQuery({ queryKey: ['admin', 'invitations-pending'], queryFn: api.listPendingInvitations })
  if (query.isPending) {
    return <Skeleton />
  }
  if (query.isError) {
    return <LoadFailure size="xs" message="The invitations could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
  }
  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-lg border border-marine/15 bg-white p-5">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 text-[10px] font-bold uppercase tracking-wide text-marine">
          Waiting to be accepted
          <EndpointHint>GET /admin/invitations</EndpointHint>
        </div>
        {query.data.length === 0 ? (
          <p className="text-xs text-ink-mid">Nobody is waiting on an invitation. An invited person is listed here until they set their password, the invitation lapses after seven days, or it is withdrawn.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-ink-mid">
                <th className="py-1 pr-3 font-bold">Who</th>
                <th className="py-1 pr-3 font-bold">As</th>
                <th className="py-1 pr-3 font-bold">Where</th>
                <th className="py-1 pr-3 font-bold">Invited by</th>
                <th className="py-1 pr-3 font-bold">Sent</th>
                <th className="py-1 font-bold">Expires</th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((invitation) => (
                <tr key={invitation.invitationId} className="border-t border-marine/10 align-top">
                  <td className="py-1.5 pr-3">
                    <div className="font-bold text-ink">
                      {invitation.firstName} {invitation.lastName}
                    </div>
                    <div className="font-mono text-[11px] text-ink-mid">{invitation.email}</div>
                  </td>
                  <td className="py-1.5 pr-3 text-ink">{invitation.kind === 'admin' && invitation.role === 'sales' ? 'Sales user' : KIND_LABEL[invitation.kind]}</td>
                  <td className="py-1.5 pr-3 text-ink-mid">
                    {invitation.about.organizationName ?? 'SeeWhoCares'}
                    {invitation.about.facilityNames.length > 0 ? ` · ${invitation.about.facilityNames.join(', ')}` : ''}
                  </td>
                  <td className="py-1.5 pr-3 text-ink-mid">{invitation.invitedBy.name ?? invitation.invitedBy.sub}</td>
                  <td className="py-1.5 pr-3 text-ink-mid">{describeSends(invitation)}</td>
                  <td className="py-1.5 text-ink-mid whitespace-nowrap">{formatDateTime(invitation.expiresAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
